import type { CompanyCustomer } from "model/Company";
import { useCallback, useState } from "react";
import { Api } from "services/api/api.service";

type UseCustomersResult = {
  customers: readonly CompanyCustomer[];
  fetchCustomers(companyId?: number): Promise<boolean>;
  isSavingCustomer: boolean;
  replaceCustomers(customers: readonly CompanyCustomer[]): void;
  saveCustomer(customer: CompanyCustomer): Promise<boolean>;
};

export const useCustomers = (): UseCustomersResult => {
  const [customers, setCustomers] = useState<readonly CompanyCustomer[]>([]);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);

  const replaceCustomers = useCallback(
    (updatedCustomers: readonly CompanyCustomer[]): void => {
      setCustomers([...updatedCustomers]);
    },
    [],
  );

  const fetchCustomers = useCallback(
    async (companyId?: number): Promise<boolean> => {
      if (!companyId) {
        setCustomers([]);
        return true;
      }

      try {
        const result = await Api().company.getCompanyCustomers(companyId);
        replaceCustomers(result.data);
        return true;
      } catch {
        return false;
      }
    },
    [replaceCustomers],
  );

  const saveCustomer = useCallback(
    async (customer: CompanyCustomer): Promise<boolean> => {
      setIsSavingCustomer(true);

      try {
        await Api().company.addCompanyCustomer(customer.companyId, customer);
        return fetchCustomers(customer.companyId);
      } catch {
        return false;
      } finally {
        setIsSavingCustomer(false);
      }
    },
    [fetchCustomers],
  );

  return {
    customers,
    fetchCustomers,
    isSavingCustomer,
    replaceCustomers,
    saveCustomer,
  };
};
